import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '../shared/Card';
import { ProgressBar } from '../shared/ProgressBar';
import { LockIcon, CheckCircleIcon, ArrowRightIcon } from 'lucide-react';
type ProfileSetupWizardProps = {
  onComplete: () => void;
};
export const ProfileSetupWizard: React.FC<ProfileSetupWizardProps> = ({
  onComplete
}) => {
  const [activeLevel, setActiveLevel] = useState(0);
  const [xp, setXp] = useState(0);
  const [completedLevels, setCompletedLevels] = useState<number[]>([]);
  const levels = [{
    id: 0,
    title: 'Inborn',
    description: 'Your natural traits and tendencies',
    xpReward: 150
  }, {
    id: 1,
    title: 'Self-Reported',
    description: 'How you perceive yourself',
    xpReward: 200
  }, {
    id: 2,
    title: 'Context',
    description: 'Your environment and background',
    xpReward: 150
  }];
  const completeLevel = (levelId: number) => {
    if (!completedLevels.includes(levelId)) {
      setCompletedLevels([...completedLevels, levelId]);
      setXp(xp + levels[levelId].xpReward);
      if (levelId < levels.length - 1) {
        setActiveLevel(levelId + 1);
      } else {
        // All levels completed
        setTimeout(() => {
          onComplete();
        }, 1000);
      }
    }
  };
  const isLevelLocked = (levelId: number) => {
    if (levelId === 0) return false;
    return !completedLevels.includes(levelId - 1);
  };
  const isLevelCompleted = (levelId: number) => {
    return completedLevels.includes(levelId);
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} className="min-h-screen w-full bg-gradient-to-b from-[#0A1128] to-[#1C2541] text-white p-4 md:p-6" data-id="element-1197">
      <div className="max-w-4xl mx-auto" data-id="element-1198">
        <div className="flex justify-between items-center mb-6" data-id="element-1199">
          <h1 className="text-2xl md:text-3xl font-bold" data-id="element-1200">Profile Setup</h1>
          <div className="flex items-center bg-[#1C2541] rounded-full py-1 px-4" data-id="element-1201">
            <span className="text-[#FFD700] mr-1" data-id="element-1202">✨</span>
            <span className="font-bold" data-id="element-1203">{xp} XP</span>
          </div>
        </div>
        <p className="text-[#B8C0E0] mb-8" data-id="element-1204">
          Complete all three levels to create your cosmic career profile
        </p>
        <div className="mb-8" data-id="element-1205">
          <div className="flex justify-between items-center mb-2" data-id="element-1206">
            <span className="text-sm text-[#B8C0E0]" data-id="element-1207">Profile Completion</span>
            <span className="text-sm text-[#B8C0E0]" data-id="element-1208">
              {completedLevels.length}/{levels.length}
            </span>
          </div>
          <ProgressBar progress={completedLevels.length / levels.length * 100} data-id="element-1209" />
        </div>
        <div className="space-y-6" data-id="element-1210">
          {levels.map(level => {
          const isActive = activeLevel === level.id;
          const isCompleted = isLevelCompleted(level.id);
          const isLocked = isLevelLocked(level.id);
          return <motion.div key={level.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: level.id * 0.2
          }} data-id="element-1211">
                <Card className={`relative ${isCompleted ? 'border-[#19D3DA]' : isActive ? 'border-[#7D56F4]' : isLocked ? 'opacity-70' : ''}`} data-id="element-1212">
                  <div className="flex items-start" data-id="element-1213">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${isCompleted ? 'bg-[#19D3DA]' : isActive ? 'bg-[#7D56F4]' : 'bg-[#3A506B]'}`} data-id="element-1214">
                      {isCompleted ? <CheckCircleIcon size={24} data-id="element-1215" /> : isLocked ? <LockIcon size={24} data-id="element-1216" /> : <span className="text-xl font-bold" data-id="element-1217">
                          {level.id + 1}
                        </span>}
                    </div>
                    <div className="flex-1" data-id="element-1218">
                      <div className="flex justify-between items-start" data-id="element-1219">
                        <div data-id="element-1220">
                          <h3 className="text-lg font-semibold" data-id="element-1221">
                            {level.title}
                          </h3>
                          <p className="text-[#B8C0E0] text-sm mb-4" data-id="element-1222">
                            {level.description}
                          </p>
                        </div>
                        <div className="bg-[#1C2541] rounded-full py-1 px-3 text-sm" data-id="element-1223">
                          +{level.xpReward} XP
                        </div>
                      </div>
                      {isActive && !isCompleted && <button onClick={() => completeLevel(level.id)} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-2 px-6 rounded-full flex items-center" data-id="element-1224">
                          <span data-id="element-1225">Start Assessment</span>
                          <ArrowRightIcon size={16} className="ml-2" data-id="element-1226" />
                        </button>}
                      {isCompleted && <div className="flex items-center text-[#19D3DA]" data-id="element-1227">
                          <CheckCircleIcon size={16} className="mr-2" data-id="element-1228" />
                          <span data-id="element-1229">Completed</span>
                        </div>}
                      {isLocked && <div className="flex items-center text-[#B8C0E0]" data-id="element-1230">
                          <LockIcon size={16} className="mr-2" data-id="element-1231" />
                          <span data-id="element-1232">Complete previous level to unlock</span>
                        </div>}
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>
        {completedLevels.length === levels.length && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mt-8 flex justify-center" data-id="element-1233">
            <button onClick={onComplete} className="bg-gradient-to-r from-[#7D56F4] to-[#19D3DA] text-white font-medium py-3 px-8 rounded-full flex items-center" data-id="element-1234">
              <span data-id="element-1235">View Your Career Matches</span>
              <ArrowRightIcon size={18} className="ml-2" data-id="element-1236" />
            </button>
          </motion.div>}
      </div>
    </motion.div>;
};